
import { AxiosInstance } from 'axios';
import { axiosInstance } from '../axios-instance';
import { ENDPOINTS } from '../endpoint';
import { BaseApiService } from './base-api.service';

export interface GroupSubscription {
  id: number;
  name: string;
  plan_type: 'BASIC' | 'ADVANCED' | 'FREE';
  tier_id?: string | null;
  current_month_parcours: number;
  max_parcours_per_month: number;
  subscription_status:
    | 'ACTIVE'
    | 'INACTIVE'
    | 'TRIAL'
    | 'CANCELLED'
    | 'PAST_DUE'
    | 'SUSPENDED'
    | 'EXPIRED';
  free_parcours_used?: boolean | null;
  free_parcours_type?: string | null;
  date_echeance?: string;
  date_subscribed?: string;
  forfait?: {
    id: number;
    name: string;
    code_forfait: string;
  };
}

export interface GroupUsageStats {
  groupe: GroupSubscription;
  monthlyParcours: number;
  maxParcours: number;
  remainingParcours: number;
}

export interface UpdateSubscriptionRequest {
  plan_type?: 'BASIC' | 'ADVANCED';
  max_parcours_per_month?: number;
  subscription_status?: 'ACTIVE' | 'INACTIVE' | 'TRIAL' | 'CANCELLED';
}

export class SubscriptionService extends BaseApiService {
  constructor(axios: AxiosInstance = axiosInstance) {
    super(ENDPOINTS.GROUPES.BASE, axios);
  }

  /**
   * Récupère les informations d'abonnement d'un groupe
   */
  async getGroupSubscription(groupeId: number): Promise<GroupSubscription> {
    const { data } = await this.axios.get(`/groupes/${groupeId}/subscription`);
    return data.data;
  }

  /**
   * Récupère les statistiques d'usage d'un groupe
   */
  async getGroupUsageStats(groupeId: number): Promise<GroupUsageStats> {
    const { data } = await this.axios.get(`/groupes/${groupeId}/usage-stats`);
    return data.data;
  }

  /**
   * Met à jour le plan d'un groupe
   */
  async updateGroupPlan(
    groupeId: number,
    updateData: UpdateSubscriptionRequest
  ): Promise<GroupSubscription> {
    const { data } = await this.axios.put(`/groupes/${groupeId}/subscription`, updateData);
    return data.data;
  }

  /**
   * Remet à zéro l'usage mensuel d'un groupe
   */
  async resetMonthlyUsage(groupeId: number): Promise<GroupUsageStats> {
    const { data } = await this.axios.post(`/groupes/${groupeId}/reset-usage`);
    return data.data;
  }

  /**
   * Met à jour l'abonnement Stripe d'un groupe
   */
  async updateStripeSubscription(
    groupeId: number,
    updateData: {
      sub_id: string;
      customer_id: string;
      forfait_id: number;
      date_echeance: string;
      date_subscribed?: string;
    }
  ): Promise<GroupSubscription> {
    const { data } = await this.axios.patch(`/groupes/${groupeId}/subscription`, updateData);
    return data.data;
  }

  /**
   * Annule l'abonnement d'un groupe
   */
  async cancelSubscription(groupeId: number): Promise<GroupSubscription> {
    const { data } = await this.axios.patch(`/groupes/${groupeId}/cancel-subscription`);
    return data.data;
  }

  /**
   * Active/désactive le mode limité d'un groupe
   */
  async toggleStrippedStatus(
    groupeId: number,
    isStripped: boolean
  ): Promise<GroupSubscription> {
    const { data } = await this.axios.patch(`/groupes/${groupeId}/toggle-stripped`, {
      isStripped
    });
    return data.data;
  }
}

export const subscriptionService = new SubscriptionService();
